import { BeakerIcon } from "@heroicons/react/24/outline";
import { CalendarDaysIcon, UserIcon } from "@heroicons/react/24/solid";
import { useScriptAssets } from "@via/api/script-assets.api.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import ProjectsFilter from "@via/pages/projects/projects-filter.tsx";
import {
  useCreateProject,
  useProjectList,
} from "@via/pages/projects/projects.api.tsx";
import { useCreateStandaloneProcessRunAttempt } from "@via/pages/projects/runs/runs.api.tsx";
import RunPermissionsSection from "@via/pages/projects/runs/run-permissions-section";
import { ModalSize } from "@via/utils/constants";
import { createRunOptions } from "@via/utils/form-validators.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import {
  PermissionType,
  ProjectViewType,
  type CreateStandaloneProcessRunAttemptRequestBody,
  type ListProjectsResponseBodyItem,
} from "common";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";

interface RunProcessModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  processId: number;
}

/**
 * The fixture manifest's path. Mirrors `FIXTURES_ASSET_PATH` in
 * `backend/src/business/process/testing/test-inputs.ts`, which is the exact path the run route
 * reads when asked to bind inputs from fixtures.
 */
const FIXTURE_MANIFEST_PATH = "tests/fixtures.yaml";

/**
 * Collects the fields the standalone run route accepts: name, project, inherit permissions and,
 * only when the process declares a fixture manifest, whether to bind the run's inputs from it.
 * No environment or directory fields, because the run detail page the user lands on collects
 * those. Mirrors create-run-modal.tsx, which collects the same three
 * fields for a pipeline based run; this modal exists separately rather than growing that one
 * with a processId branch, since the two post to different routes with different bodies
 * (this one has no pipelineId at all, the pipeline is synthesized server side).
 */
type FormRunAttemptRequestBody = Omit<
  CreateStandaloneProcessRunAttemptRequestBody,
  "projectId"
> & {
  projectId: number | undefined;
};

const sort = [
  {
    id: "createdAt",
    desc: true,
  },
];

const RunProcessModal: FC<RunProcessModalProps> = ({
  open,
  setOpen,
  processId,
}) => {
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativeProjects, setCumulativeProjects] = useState<
    ListProjectsResponseBodyItem[]
  >([]);
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >([{ id: "type", value: ProjectViewType.MyProjects }]);

  const [projectsPage, setProjectsPage] = useState(0);
  const projectOptionSize = 10;

  const { data: projects, isPending: isProjectsLoading } = useProjectList(
    projectsPage,
    projectOptionSize,
    sort,
    [...columnFilters, { id: "name", value: searchedInput }]
  );

  const { mutateAsync: createRun, isPending: isCreateRunLoading } =
    useCreateStandaloneProcessRunAttempt(processId);

  const { resetChangeCount } = useChangesDetect();

  // The same asset list the process editor's Files tab loads, so the checkbox appears exactly
  // when the run route would find a manifest to bind from.
  const { data: scriptAssets } = useScriptAssets(processId);
  const hasFixtureManifest = useMemo(
    () =>
      (scriptAssets ?? []).some(asset => FIXTURE_MANIFEST_PATH === asset.path),
    [scriptAssets]
  );

  const fetchMoreProjects = useCallback(() => {
    setProjectsPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((projects?.total ?? 0) / projectOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [projects?.total, projectOptionSize]);

  useEffect(() => {
    setCumulativeProjects([]);
  }, [columnFilters]);

  useEffect(() => {
    if (!projects) return;

    setCumulativeProjects(prevProjects => {
      const newData = projects.data ?? [];

      if (!newData.length) return [];

      // Append new results while preventing duplicates
      const existingIds = new Set(prevProjects.map(p => p.id));
      const newProjects = newData.filter(p => !existingIds.has(p.id));

      return [...prevProjects, ...newProjects];
    });
  }, [projects?.data, projectsPage, searchedInput, columnFilters]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setProjectsPage(0);
    setSearchedInput(value);
  }, 300);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormRunAttemptRequestBody>({
    mode: "onChange",
    defaultValues: {
      name: "",
      projectId: undefined,
      inheritFromProject: true,
      bindInputsFromFixtures: false,
    },
  });

  const {
    mutate: createProject,
    data: createProjectData,
    isPending: isCreateProjectLoading,
    isSuccess: isCreateProjectSuccess,
  } = useCreateProject();

  useEffect(() => {
    if (createProjectData) {
      const { id } = createProjectData;
      setValue("projectId", id, { shouldValidate: true });
    }
  }, [isCreateProjectSuccess, createProjectData]);

  // Reports back to Modal, which owns the close: false keeps the dialog open on invalid input, a
  // failed request rejects (handleSubmit rethrows) and also keeps it open, true closes it. On
  // success the run hook also navigates to the new run's page.
  const handleConfirmModal = async (): Promise<boolean> => {
    let created = false;
    await handleSubmit(
      async ({
        bindInputsFromFixtures,
        ...rest
      }: FormRunAttemptRequestBody): Promise<void> => {
        // Send the flag only when it is offered and ticked. Unticked is the route's own default,
        // so an ordinary launch posts the same body it always has.
        const payload =
          hasFixtureManifest && true === bindInputsFromFixtures
            ? { ...rest, bindInputsFromFixtures: true }
            : rest;
        // Before the request, not after it: the hook navigates in its onSuccess, and the pipeline
        // page's unsaved changes blocker reads the count from the last render, so resetting after
        // the await would stop that navigation.
        resetChangeCount();
        await createRun(
          payload as CreateStandaloneProcessRunAttemptRequestBody
        );
        created = true;
      }
    )();
    return created;
  };

  const handleCreateNewProject = (name: string): void => {
    createProject({
      name,
      permission: PermissionType.UserOwned,
      summary: "",
    });
  };

  const projectListOptions = useMemo(() => {
    const map = new Map<number, ListProjectsResponseBodyItem>();
    cumulativeProjects.forEach(project => map.set(project.id, project));
    const mergedProjects = Array.from(map.values());

    return (
      mergedProjects?.map(({ name, id, owner, updatedAt }) => ({
        label: name,
        value: id,
        metadata: { name, owner, updatedAt },
      })) ?? []
    );
  }, [cumulativeProjects]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Run Process"
      confirmText="Create"
      size={ModalSize.Large}
      isConfirmLoading={isCreateRunLoading}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <div className="my-5">
          <div className="mt-2">
            <ProjectsFilter
              hideInputFilters
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setProjectsPage}
              setPageSize={() => {}}
            />
            <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
              Choose Project for Run<span className="text-red-400">*</span>
            </label>
            <div className="mb-4">
              <SelectComponent
                control={control}
                name="projectId"
                rules={createRunOptions.projectId}
                options={projectListOptions}
                isLoading={isProjectsLoading || isCreateProjectLoading}
                placeholder={"Choose a Project or Type to Create New"}
                prefixIcon={<BeakerIcon />}
                isCreatable={true}
                onCreateOption={handleCreateNewProject}
                onFetchMoreOptions={fetchMoreProjects}
                onInputChange={inputValue =>
                  debouncedHandleInputChange(inputValue)
                }
                formatOptionLabel={(option, { context }) =>
                  context === "menu" ? (
                    <div className="flex flex-col gap-1">
                      {/* Project Name */}
                      <span className="font-semibold text-black">
                        {option.metadata?.name || option.label}
                      </span>

                      {option.metadata && (
                        <div className="text-sm text-gray-600">
                          {/* Owner */}
                          <div className="flex items-center gap-1">
                            <UserIcon className="h-4 w-4 text-indigo-600" />
                            <span className="font-medium">Owner:</span>
                            <span>{option.metadata.owner}</span>
                          </div>

                          {/* Last Updated Date */}
                          <div className="flex items-center gap-1">
                            <CalendarDaysIcon className="h-4 w-4 text-red-600" />
                            <span className="font-medium">Last updated:</span>
                            <span>
                              {formatDateString(
                                option.metadata.updatedAt,
                                "LLL"
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>{option.label}</span>
                  )
                }
              />
              {errors.projectId && (
                <span className="text-xs text-rose-500">
                  {errors.projectId.message}
                </span>
              )}
            </div>
            <FormInputComponent
              name="name"
              label="Run Name"
              showRequiredField
              control={control}
              validations={createRunOptions.name}
            />
            {hasFixtureManifest && (
              <label className="mt-4 flex items-start gap-3">
                <input
                  type="checkbox"
                  {...register("bindInputsFromFixtures")}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <span className="text-sm leading-6">
                  <span className="block font-medium text-gray-900">
                    Bind inputs from test fixtures
                  </span>
                  <span className="block text-gray-600">
                    Fill this run&apos;s inputs from tests/fixtures.yaml. The
                    run is refused if a fixture names an unknown input or a
                    fixture file has moved or changed since it was recorded.
                  </span>
                </span>
              </label>
            )}
          </div>

          {/* Permissions Section */}
          <RunPermissionsSection watch={watch} setValue={setValue} />
        </div>
      </form>
    </Modal>
  );
};

export default RunProcessModal;
